
<template>
  <div class="container mx-auto my-8 bg-white py-2 rounded-lg">
    <!-- Project Selector Area -->
    <!-- <div
      class="flex flex-col md:flex-row items-start md:items-center justify-between px-3 py-4 border-b border-gray-100">
      <h2 class="text-lg font-semibold text-bodyText mb-3 md:mb-0">Teams</h2> 
       <div class="w-full md:w-auto">
        <CustomSelect :options="projects" v-model="selectedProjectId" labelKey="name" valueKey="id"
          placeholder="Select a project" @change="onProjectChange" class="w-full md:w-56" />
      </div>
    </div> -->

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-8">
      <div
        class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDarkBg"
      ></div>
    </div>

    <!-- Error State -->
    <!-- <div v-else-if="error" class="p-6 text-center text-red-500">
      {{ error }}
      <button @click="fetchTeams" class="mt-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-md">
        Try Again
      </button>
    </div> -->

    <div v-else-if="teams.length === 0" class="text-center py-16">
      <p
        class="mx-auto bg-primaryLight px-2 py-24 border border-grayLight rounded-[16px]"
      >
        Teams not found.
      </p>
    </div>
    

    <div v-else class="overflow-x-auto text-grayDark">
      <div>
        <table class="min-w-full divide-y">
          <thead class="text-[12px]">
            <tr>
              <th class="px-4 py-3 text-left font-medium uppercase tracking-wider">
                Team Name
              </th>
              <th class="px-4 py-3 text-left font-medium uppercase tracking-wider">
                Members
              </th>
              <th class="px-4 py-3 text-left font-medium uppercase tracking-wider">
                Status
              </th>
              <th class="px-4 py-3 text-left font-medium uppercase tracking-wider">
                Created
              </th>
              <th class="px-4 py-3 text-left font-medium uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="team in teams" :key="team.team_id" class="hover:bg-gray-50">
              <td class="px-4 py-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div
                      class="h-10 w-10 rounded-full bg-primaryDarkBg flex items-center justify-center text-white font-semibold text-[12px]"
                    >
                      {{ getInitials(team.team_name) }}
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900">
                      {{ team.team_name }}
                    </div>
                    <div v-if="team.description" class="text-sm text-gray-500">
                      {{ team.description }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-4 py-4">
                <div class="text-sm text-gray-900">
                  {{ team.member_count || 0 }} member(s)
                </div>
              </td>
              <td class="px-4 py-4">
                <span
                  :class="[
                    'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                    team.status && team.status.toLowerCase() === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800',
                  ]"
                >
                  {{ capitalize(team.status || "Active") }}
                </span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-500">
                {{ formatDate(team.created_at) }}
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex space-x-3">
                  <button
                    @click="openEditTeams(team)"
                    class="text-grayDark hover:text-primaryDarkBg"
                  >
                    <svg
                      class="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </button>
                  <button
                    @click="confirmDeleteTeam(team)"
                    class="text-red-600 hover:text-red-900"
                  >
                    <svg
                      class="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div
          v-if="teams.length > 0"
          class="flex justify-between items-center px-4 py-3 border-t"
        >
          <div class="text-sm text-gray-500">
            Showing {{ startItem }} to {{ endItem }} of
            {{ totalItems }} results
          </div>

          <div class="flex space-x-2">
            <button
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Team Modal -->
    <div v-if="showModal" class="modal-container">
      <div class="fixed inset-0 z-40 bg-black opacity-50"></div>

      <div
        class="modal-content max-w-2xl w-full max-h-[90vh] relative bg-white m-auto flex-col flex rounded-lg top-0 z-50"
      >
        <div
          class="w-full border-b-[1px] mb-4 px-6 py-4 flex justify-between items-center"
        >
          <h2 class="text-[13px] text-grayDark font-medium">
            Edit Team - {{ selectedProjectName }}
          </h2>
          <h2 class="text-[13px] text-grayDark font-medium">Re-assign team</h2>
          <!-- <button type="submit" class="text-[12px] font-medium py-2 px-4 border border-grayLight text-grayDark rounded disabled:opacity-50">
            Save Changes
          </button> -->
          <div class="flex justify-end pt-2">
            <button
              @click="updateTeamInfo"
              :disabled="updatingTeam"
              class="px-4 py-2 bg-grayDark text-white text-[12px] rounded-md disabled:opacity-50"
            >
              {{
                updatingTeam ? "Saving Changes..." : "Save Changes"
              }}
            </button>
          </div>

          <!-- <button @click="closeModal" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button> -->
        </div>

        <div v-if="loadingTeamDetails" class="p-6 text-center">
          <div class="flex justify-center">
            <div
              class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDarkBg"
            ></div>
          </div>
        </div>

        <div v-else-if="teamDetailsError" class="p-6 text-center text-red-500">
          {{ teamDetailsError }}
          <button
            @click="fetchTeamDetails"
            class="mt-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-md"
          >
            Try Again
          </button>
        </div>

        <div v-else class="overflow-y-auto px-6">
          

          <!-- Team Info Form -->
          <form @submit.prevent="updateTeamInfo" class="mb-6">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Team Name</label
              >
              <input
                v-model="selectedTeam.team_name"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primaryDark"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Description</label
              >
              <textarea
                v-model="selectedTeam.description"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primaryDark"
              ></textarea>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >Status</label
              >
              <select
                v-model="selectedTeam.status"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primaryDark"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>

          <!-- Team Members Section -->
          <div class="">
            <h3 class="text-sm font-medium text-gray-700 mb-3">Team Members</h3>
            <div
              v-if="
                !selectedTeam.members || selectedTeam.members.length === 0
              "
              class="text-center py-4 text-gray-500"
            >
              No members found
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="member in selectedTeam.members"
                :key="member.team_member_id"
                class="flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div class="flex items-center">
                  <div
                    class="h-10 w-10 rounded-full bg-primaryDarkBg flex items-center justify-center text-white font-semibold text-[12px]"
                  >
                    {{ getInitials(member.member_name) }}
                  </div>
                  <div class="ml-3">
                    <div class="text-sm font-medium text-gray-900">
                      {{ member.member_name }}
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ capitalize(member.member_role) }}
                    </div>
                  </div>
                </div>
                <button
                  @click="confirmRemoveMember(member)"
                  :disabled="removingMember === member.team_member_id"
                  class="text-red-600 hover:text-red-900 disabled:opacity-50"
                >
                  <svg
                    class="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end p-6 border-t mt-4">
          <button
            @click="closeModal"
            class="mr-3 py-2 px-4 bg-gray-100 text-gray-700 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirmModal" class="modal-container">
      <div class="fixed inset-0 z-40 bg-black opacity-50"></div>

      <div
        class="modal-content max-w-md w-full relative bg-white m-auto flex-col flex rounded-lg z-50 p-6 text-[13px]"
      >
        <h3 class="text-[13px] font-medium text-grayDark mb-3">
          Confirm Delete
        </h3>
        <p class="mb-6">
          Are you sure you want to delete the team "{{ deleteTeam.team_name }}"?
          This action cannot be undone.
        </p>

        <div class="flex justify-end">
          <button
            @click="showDeleteConfirmModal = false"
            class="mr-3 py-2 px-4 bg-gray-100 text-gray-700 rounded"
          >
            Cancel
          </button>
          <button
            @click="deleteTeamConfirmed"
            :disabled="deletingTeam"
            class="py-2 px-4 bg-grayDark text-white rounded disabled:opacity-50"
          >
            {{
              deletingTeam ? "Deleting..." : "Delete"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Remove Member Confirmation Modal -->
    <div v-if="showRemoveMemberModal" class="modal-container">
      <div class="fixed inset-0 z-40 bg-black opacity-50"></div>

      <div
        class="modal-content max-w-md w-full relative bg-white m-auto flex-col flex rounded-lg z-50 p-6"
      >
        <h3 class="text-[13px] font-medium text-grayDark mb-3">
          Confirm Remove Member
        </h3>
        <p class="mb-6">
          Are you sure you want to remove "{{ removeMember.member_name }}" from
          this team?
        </p>

        <div class="flex justify-end">
          <button
            @click="showRemoveMemberModal = false"
            class="mr-3 py-2 px-4 bg-gray-100 text-gray-700 rounded"
          >
            Cancel
          </button>
          <button
            @click="removeMemberConfirmed"
            :disabled="removingMember"
            class="py-2 px-4 bg-primaryDark text-white rounded disabled:opacity-50"
          >
            {{
              removingMember ? "Removing..." : "Remove"
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
// import CustomSelect from './CustomSelect.vue';

export default {
  name: "AllTeams",
  components: {
    // CustomSelect,
    // SkeletonLoader: {
    //   props: {
    //     width: String,
    //     height: String,
    //   },
    //   template: `
    //     <div 
    //       :style="{ width, height }" 
    //       class="bg-gray-200 animate-pulse rounded-md"
    //     ></div>
    //   `,
    // },
  },
  data() {
    return {
      showModal: false,
      showDeleteConfirmModal: false,
      showRemoveMemberModal: false,
      projects: [],
      teams: [],
      selectedProjectId: null,
      selectedProjectName: "",
      selectedTeam: {
        team_id: null,
        team_name: "",
        description: "",
        status: "Active",
        members: [],
      },
      deleteTeam: {
        team_id: null,
        team_name: "",
      },
      removeMember: {
        team_member_id: null,
        member_name: "",
      },
      loading: false,
      loadingTeamDetails: false,
      teamDetailsError: null,
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      totalPages: 1,
      updatingTeam: false,
      deletingTeam: false,
      removingMember: null,
    };
  },
  computed: {
    startItem() {
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.pageSize, this.totalItems);
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
  },
  methods: {
    openEditTeams(team) {
      this.selectedTeam = {
        team_id: team.team_id,
        team_name: team.team_name,
        description: team.description || "",
        status: team.status || "Active",
        members: [],
      };

      // Get the selected project name
      const project = this.projects.find((p) => p.id == this.selectedProjectId);
      this.selectedProjectName = project ? project.name : "";

      this.showModal = true;
      this.fetchTeamDetails();
    },

    async fetchTeamDetails() {
      if (!this.selectedTeam.team_id) return;

      this.loadingTeamDetails = true;
      this.teamDetailsError = null;

      try {
        const response = await apiClient.get(
          `/web/teams/${this.selectedTeam.team_id}`
        );

        if (response.data && response.data.data) {
          const teamData = response.data.data;
          this.selectedTeam = {
            ...this.selectedTeam,
            members: teamData.members || [],
            member_count: teamData.member_count || 0,
          };
          console.log("Loaded team details:", this.selectedTeam);
        } else {
          this.$toast.error("Failed to load team details");
        }
      } catch (error) {
        console.error("Error fetching team details:", error);
        this.$toast.error(
          error.response?.data?.message || "Failed to load team details"
        );
      } finally {
        this.loadingTeamDetails = false;
      }
    },

    async updateTeamInfo() {
      this.updatingTeam = true;

      try {
        const response = await apiClient.put(
          `/web/teams/${this.selectedTeam.team_id}`,
          {
            team_name: this.selectedTeam.team_name,
            description: this.selectedTeam.description,
            status: this.selectedTeam.status,
          }
        );

        if (response.data && response.data.status_code === 200) {
          this.$toast.success("Team updated successfully");

          // Update the team in the list
          const index = this.teams.findIndex(
            (t) => t.team_id === this.selectedTeam.team_id
          );
          if (index !== -1) {
            this.teams[index] = {
              ...this.teams[index],
              team_name: this.selectedTeam.team_name,
              description: this.selectedTeam.description,
              status: this.selectedTeam.status,
            };
          }
        } else {
          this.$toast.error("Failed to update team");
        }
      } catch (error) {
        console.error("Error updating team:", error);
        this.$toast.error(
          error.response?.data?.message || "Failed to update team"
        );
      } finally {
        this.updatingTeam = false;
      }
    },

    confirmDeleteTeam(team) {
      this.deleteTeam = {
        team_id: team.team_id,
        team_name: team.team_name,
      };
      this.showDeleteConfirmModal = true;
    },

    async deleteTeamConfirmed() {
      this.deletingTeam = true;

      try {
        const response = await apiClient.delete(
          `/web/teams/${this.deleteTeam.team_id}`
        );

        if (response.data && response.data.status_code === 200) {
          // Remove the team from the list
          this.teams = this.teams.filter(
            (t) => t.team_id !== this.deleteTeam.team_id
          );
          this.showDeleteConfirmModal = false;

          // Show a temporary success message
          this.$toast.success("Team deleted successfully");
        } else {
          this.$toast.error("Failed to delete team");
        }
      } catch (error) {
        console.error("Error deleting team:", error);
        this.$toast.error(
          error.response?.data?.message || "Failed to delete team"
        );
      } finally {
        this.deletingTeam = false;
      }
    },

    confirmRemoveMember(member) {
      this.removeMember = {
        team_member_id: member.team_member_id,
        member_name: member.member_name,
      };
      this.showRemoveMemberModal = true;
    },

    async removeMemberConfirmed() {
      if (!this.removeMember.team_member_id) return;

      this.removingMember = this.removeMember.team_member_id;

      try {
        const response = await apiClient.delete(
          `/web/teams/${this.selectedTeam.team_id}/members/${this.removeMember.team_member_id}`
        );

        if (response.data && response.data.status_code === 200) {
          // Remove the member from the team
          this.selectedTeam.members = this.selectedTeam.members.filter(
            (m) => m.team_member_id !== this.removeMember.team_member_id
          );

          // Update the member count
          this.selectedTeam.member_count = this.selectedTeam.members.length;

          // Update the team in the list
          const index = this.teams.findIndex(
            (t) => t.team_id === this.selectedTeam.team_id
          );
          if (index !== -1) {
            this.teams[index].member_count = this.selectedTeam.member_count;
          }

          this.showRemoveMemberModal = false;
          this.$toast.success(`${this.removeMember.member_name} removed from team successfully`);
        } else {
          this.$toast.error("Failed to remove team member");
        }
      } catch (error) {
        console.error("Error removing team member:", error);
        this.$toast.error(
          error.response?.data?.message || "Failed to remove team member"
        );
      } finally {
        this.removingMember = null;
      }
    },

    closeModal() {
      this.showModal = false;
    },

    getInitials(name) {
      if (!name) return "";

      const parts = name.split(" ");
      if (parts.length > 1) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return parts[0].charAt(0).toUpperCase();
    },

    capitalize(text) {
      if (!text) return "";
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    async fetchProjects() {
      try {
        this.loading = true;
        this.error = null;

        let response;
        if (this.isManager) {
          response = await apiClient.get("/web/manager/me");
          if (response.data && response.data.data) {
            this.projects = response.data.data.projects || [];
          } else {
            this.error = "Failed to load projects";
          }
        } else if (this.isAdmin) {
          response = await apiClient.get("/web/projects_all");
          if (response.data && response.data.data) {
            this.projects = response.data.data;
          } else {
            this.error = "Failed to load projects";
          }
        } else if (this.isTalent) {
          // Talents view teams directly, no project selection needed
        } else {
          // Unknown user type - no projects to fetch
          console.log("Unknown user type");
        }

        console.log(
          // `[${
          //   this.isAdmin ? "Admin" : this.isManager ? "Manager" : "Unknown"
          // }] Projects fetched:`,
          // this.projects.length,
          // "projects:",
          // this.projects.map((p) => ({
          //   id: p.id || p.project_id,
          //   name: p.name || p.project_name,
          // }))
        );

        if (this.projects.length === 0) {
          // this.error = "No projects available for your account";
        }
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Failed to load projects");
        this.projects = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchTeams() {
      // if (!this.selectedProjectId) {
      //   this.teams = [];
      //   return;
      // }

      try {
        this.loading = true;

        // const url = `/web/teams/project/${this.selectedProjectId}?page=${this.currentPage}&page_size=${this.pageSize}`;

        
        const response = await apiClient.get("/web/teams/manager/teams");

        this.teams = response.data.data;
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Failed to load teams");
        this.teams = [];
      } finally {
        this.loading = false;
      }
    },

    onProjectChange(project) {
      console.log("Project changed:", project);
      this.currentPage = 1; // Reset to first page when changing projects

      localStorage.setItem("selectedProjectId", this.selectedProjectId);

      // Get the selected project name
      const selectedProject = this.projects.find(
        (p) => p.id == this.selectedProjectId
      );
      this.selectedProjectName = selectedProject ? selectedProject.name : "";

      this.fetchTeams();
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;

      this.currentPage = page;
      this.fetchTeams();
    },
  },
  created() {
    this.fetchTeams()
    this.fetchProjects().then(() => {
      const storedProjectId = localStorage.getItem("selectedProjectId");
      if (
        storedProjectId &&
        this.projects.some((p) => p.id == storedProjectId)
      ) {
        this.selectedProjectId = storedProjectId;
        this.onProjectChange();
      }
    });
  },
};
</script>

<style scoped>
.modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: 50;
}

.smoke-light {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-content {
  position: relative;
  z-index: 60;
}
</style>
